import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FloorPlanRoomType } from "../../utils/floorPlanHelpers";
import styles from "./SideNavigation.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

export interface SideNavigationProps {
  roomTypes: FloorPlanRoomType[];
  activeRoomTypeId: string | null;
  onRoomTypeSelect: (id: string) => void;
}

const SideNavigation = ({ roomTypes, activeRoomTypeId, onRoomTypeSelect }: SideNavigationProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [showFade, setShowFade] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowFade(hasOverflow && !isAtBottom);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", checkOverflow);
    window.addEventListener("resize", checkOverflow);
    return () => {
      el.removeEventListener("scroll", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow, roomTypes]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      let targetIndex: number | null = null;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        targetIndex = index < roomTypes.length - 1 ? index + 1 : 0;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        targetIndex = index > 0 ? index - 1 : roomTypes.length - 1;
      } else if (e.key === "Home") {
        e.preventDefault();
        targetIndex = 0;
      } else if (e.key === "End") {
        e.preventDefault();
        targetIndex = roomTypes.length - 1;
      }

      if (targetIndex !== null) {
        buttonRefs.current[targetIndex]?.focus();
        onRoomTypeSelect(roomTypes[targetIndex].id);
      }
    },
    [roomTypes, onRoomTypeSelect],
  );

  // Determine which room type index is currently active
  const activeIndex = useMemo(
    () => roomTypes.findIndex((rt) => rt.id === activeRoomTypeId),
    [roomTypes, activeRoomTypeId],
  );
  const { t } = useTranslationHook();

  return (
    <nav
      className={styles.container}
      aria-label="Floor plan navigation"
      data-testid="floor-plans-side-navigation"
    >
      <div ref={scrollRef} className={styles.scrollArea}>
        <div className={styles.separator}>{t("Floor")}</div>
        {roomTypes.map((roomType, idx) => {
          const isActive = activeRoomTypeId === roomType.id;
          return (
            <button
              type="button"
              key={roomType.id}
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              className={`${styles.roomTypeButton} ${isActive ? styles.active : ""}`.trim()}
              onClick={() => onRoomTypeSelect(roomType.id)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              aria-label={`Select ${roomType.name} floor plan`}
              aria-current={isActive ? "true" : undefined}
              tabIndex={activeIndex === idx || (activeIndex === -1 && idx === 0) ? 0 : -1}
              data-testid={`floor-plans-room-type-${roomType.id}`}
            >
              {roomType.name}
            </button>
          );
        })}
      </div>
      {showFade && <div className={styles.fadeOverlay} aria-hidden="true" />}
    </nav>
  );
};

export default wrapperHOC(SideNavigation, {
  componentName: "SideNavigation-FloorPlansSection-PropertyPage",
  showForChina: true,
});
